import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, AtSign, Eye, EyeOff, LifeBuoy } from "lucide-react";
import { ContactSupportModal } from "@/components/ContactSupportModal";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Brand } from "@/components/common";
import { Button } from "@/components/Button";
import { Field, TextInput } from "@/components/Modal";

export default function AuthPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [form, setForm] = useState({
    identifier: "", name: "", email: "", username: "", password: "", remember: true,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const afterAuth = async () => {
    if (inviteToken) {
      try {
        const { data } = await api.post(`/connections/invite/${inviteToken}/accept`);
        toast.success("Connected!");
        nav(`/connections/${data.connection_id}`);
        return;
      } catch {
        // fall through to profile if invite invalid
      }
    }
    nav("/profile");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.identifier, form.password, form.remember);
        await afterAuth();
      } else if (mode === "register") {
        await register({ name: form.name, email: form.email, username: form.username, password: form.password });
        toast.success("Welcome to InfoKeepSake! Check your email to verify your account. (Check your Spam/Junk folder too.)", { duration: 8000 });
        await afterAuth();
      } else {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link is on its way. (Check your Spam/Junk folder too.)", { duration: 8000 });
        setMode("login");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left / hero */}
      <div className="hidden md:flex md:w-1/2 relative bg-[#0B0F19] items-center justify-center p-12 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-10 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative max-w-md">
          <Brand size={72} />
          <h1 className="font-display font-extrabold text-4xl mt-8 text-slate-50 leading-tight">
            Remember what matters.
          </h1>
          <p className="text-slate-400 mt-4 text-base leading-relaxed">
            Keep everything worth remembering about yourself — and privately share just the right
            things with the people you're connected to. Partners, family, friends, roommates.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {["Your own private profile", "Per-connection privacy control", "Shared memories, savings & plans", "Powerful search that respects privacy"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right / form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm iks-fade-up">
          <div className="md:hidden mb-8 flex justify-center"><Brand size={64} /></div>
          <h2 className="font-display font-bold text-2xl text-slate-50">
            {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset password"}
          </h2>
          <p className="text-slate-400 text-sm mt-1.5 mb-7">
            {mode === "login"
              ? "Log in with your email or username."
              : mode === "register"
              ? "Just the essentials — set up your profile later."
              : "We'll email you a secure reset link. Don't see it? Check your Spam/Junk folder."}
          </p>

          <form onSubmit={submit}>
            {mode === "register" && (
              <>
                <Field label="Name">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <TextInput required value={form.name} onChange={set("name")} placeholder="Your name" className="pl-9" data-testid="register-name" />
                  </div>
                </Field>
                <Field label="Username">
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <TextInput required value={form.username} onChange={set("username")} placeholder="unique_handle" className="pl-9" data-testid="register-username" />
                  </div>
                </Field>
              </>
            )}

            {(mode === "register" || mode === "forgot") && (
              <Field label="Email">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <TextInput required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="pl-9" data-testid="auth-email" />
                </div>
              </Field>
            )}

            {mode === "login" && (
              <Field label="Email or Username">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <TextInput required value={form.identifier} onChange={set("identifier")} placeholder="you@email.com or handle" className="pl-9" data-testid="login-identifier" />
                </div>
              </Field>
            )}

            {mode !== "forgot" && (
              <Field label="Password">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <TextInput
                    required
                    type={showPw ? "text" : "password"}
                    value={form.password}
                    onChange={set("password")}
                    placeholder="••••••••"
                    className="pl-9 pr-9"
                    data-testid="auth-password"
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Field>
            )}

            {mode === "login" && (
              <div className="flex items-center justify-between mb-5 -mt-1">
                <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.remember}
                    onChange={(e) => setForm((f) => ({ ...f, remember: e.target.checked }))}
                    className="rounded border-white/20 bg-transparent text-blue-600 focus:ring-sky-400"
                    data-testid="remember-me"
                  />
                  Keep me logged in
                </label>
                <button type="button" onClick={() => setMode("forgot")} className="text-sm text-sky-400 hover:text-sky-300" data-testid="forgot-link">
                  Forgot?
                </button>
              </div>
            )}

            <Button type="submit" full loading={busy} className="mt-1" data-testid="auth-submit">
              {mode === "login" ? "Log in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            {mode === "login" ? (
              <>New here?{" "}
                <button onClick={() => setMode("register")} className="text-sky-400 hover:text-sky-300 font-medium" data-testid="switch-register">
                  Create an account
                </button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button onClick={() => setMode("login")} className="text-sky-400 hover:text-sky-300 font-medium" data-testid="switch-login">
                  Log in
                </button>
              </>
            )}
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 text-center">
            <button onClick={() => setSupportOpen(true)} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-sky-300 transition-colors" data-testid="contact-support-btn">
              <LifeBuoy size={15} /> Need help? Contact Support
            </button>
          </div>
        </div>
      </div>
      <ContactSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}
